import React, { useRef, useState, useEffect } from 'react';
import Hls from 'hls.js';
import './VideoPlayer.css';

const VideoPlayer = ({ camera, streamUrl }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hlsRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const toggleFullscreen = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const video = videoRef.current;
    if (!video) return;

    // iOS Safari: only the video element's webkitEnterFullscreen works for inline video
    if (typeof video.webkitEnterFullscreen === 'function') {
      video.webkitEnterFullscreen();
      return;
    }
    // Standard Fullscreen API (desktop, Android)
    if (document.documentElement.requestFullscreen) {
      video.requestFullscreen?.();
    }
  };

  const handleLoadedMetadata = () => {
    setLoading(false);
    setError(null);
    const video = videoRef.current;
    if (video) {
      // Try to seek to live edge for live streams
      if (video.duration === Infinity || !isFinite(video.duration)) {
        // Live stream - seek to a large number (will be clamped to live edge)
        video.currentTime = 999999;
      } else if (video.duration > 0) {
        // VOD or finite duration - seek to end
        video.currentTime = video.duration;
      }
      // Don't call play() here - autoplay attribute handles it
      // This prevents "interrupted by pause()" errors
    }
  };

  // Setup HLS.js when streamUrl changes
  useEffect(() => {
    if (!streamUrl || !videoRef.current) return;

    const video = videoRef.current;
    let hls = null;

    // Always use HLS.js for better compatibility and error handling
    // Even Safari can benefit from HLS.js for better error recovery
    if (Hls.isSupported()) {
      // Use HLS.js for browsers that don't support native HLS
      console.log('Using HLS.js for playback');
      hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,          // Disable low latency for smoother playback
        liveSyncDurationCount: 5,       // Stay 5 segments (5s) behind live edge
        liveMaxLatencyDurationCount: 10, // Max 10 segments behind before seeking
        liveDurationInfinity: true,     // Treat as infinite live stream
        liveBackBufferLength: 10,       // Keep 10s back buffer
        backBufferLength: 10,           // 10s back buffer
        maxBufferLength: 10,            // Buffer 10s ahead
        maxMaxBufferLength: 20,         // Max buffer 20s
        maxBufferSize: 30 * 1000 * 1000, // 30MB max buffer
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 2,
        nudgeOffset: 0.1,
        nudgeMaxRetry: 5,
        startFragPrefetch: true,        // Prefetch start fragment
        maxFragLoadingTimeOut: 15000,
        fragLoadingTimeOut: 10000,
        manifestLoadingTimeOut: 10000,
        manifestLoadingMaxRetry: 3,
        levelLoadingTimeOut: 10000,
        levelLoadingMaxRetry: 3,
        fragLoadingMaxRetry: 4,
        startLevel: 0,                  // Start at first level immediately
        capLevelToPlayerSize: false,
        testBandwidth: false,           // Don't test bandwidth (faster startup)
        progressive: true,
        xhrSetup: (xhr, url) => {
          xhr.withCredentials = false;
        }
      });
      
      hls.loadSource(streamUrl);
      hls.attachMedia(video);
      
      let hasStartedPlaying = false;
      
      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        if (data.levels && data.levels.length > 0) {
          hls.currentLevel = 0;
        }
        // Don't play yet - wait for buffer
        console.log('Waiting for 5s buffer before playing...');
      });
      
      // Check buffer level and start playing when we have enough
      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        if (hasStartedPlaying) return;
        
        // Check how much is buffered
        const buffered = video.buffered;
        if (buffered.length > 0) {
          const bufferEnd = buffered.end(buffered.length - 1);
          const bufferStart = buffered.start(0);
          const bufferLength = bufferEnd - bufferStart;
          
          console.log(`Buffer: ${bufferLength.toFixed(1)}s`);
          
          // Wait for 5 seconds of buffer before playing
          if (bufferLength >= 5) {
            hasStartedPlaying = true;
            setLoading(false);
            setError(null);
            
            // Seek to live sync position (5 segments behind live)
            if (hls.liveSyncPosition) {
              console.log('Seeking to live sync position:', hls.liveSyncPosition);
              video.currentTime = hls.liveSyncPosition;
            }
            
            console.log('Buffer ready, starting playback');
            video.play().catch((err) => {
              if (err.name !== 'NotAllowedError') {
                console.warn('Playback error:', err);
              }
            });
          }
        }
      });
      
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS error:', data);
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('HLS netwerkfout, proberen te herstellen...');
              try {
                hls.startLoad();
              } catch (err) {
                console.error('Herstellen van netwerkfout mislukt:', err);
                setError('Netwerkfout bij laden stream');
              }
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error('HLS media fout, proberen te herstellen...');
              try {
                hls.recoverMediaError();
              } catch (err) {
                console.error('Herstellen van media fout mislukt:', err);
                // Probeer stream opnieuw te laden
                setTimeout(() => {
                  hls.loadSource(streamUrl);
                  hls.startLoad();
                }, 1000);
              }
              break;
            default:
              console.error('HLS fatale fout:', data);
              const errorMsg = data.details || data.message || 'Onbekende HLS fout';
              console.error('HLS fout details:', errorMsg);
              setError(`HLS fout: ${errorMsg}`);
              // Don't destroy immediately - try to reload
              setTimeout(() => {
                try {
                  hls.loadSource(streamUrl);
                  hls.startLoad();
                } catch (err) {
                  console.error('Failed to reload stream:', err);
                  hls.destroy();
                }
              }, 2000);
              break;
          }
        } else {
          // Non-fatal errors, just log
          console.warn('HLS non-fatal error:', data);
        }
      });
      
      hlsRef.current = hls;
    } else {
      setError('HLS wordt niet ondersteund in deze browser');
      return;
    }

    // Cleanup function
    return () => {
      if (hls) {
        hls.destroy();
        hlsRef.current = null;
      }
    };
  }, [streamUrl]);

    const downloadSnapshot = async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      return;
    }
    setDownloading(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('toBlob failed'))),
          'image/jpeg',
          0.92
        );
      });
      if (!blob) {
        setDownloading(false);
        return;
      }
      const name = (camera?.name || 'snapshot').replace(/[^a-z0-9-_]/gi, '-');
      const date = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const filename = `snapshot-${name}-${date}.jpg`;
      const file = new File([blob], filename, { type: 'image/jpeg' });

      // On mobile only: use Web Share API so user can choose "Save Image" / "Add to Photos" → camera roll.
      // On PC always use direct download.
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const useShare = isMobile && navigator.share && navigator.canShare?.({ files: [file] });

      if (useShare) {
        try {
          await navigator.share({
            files: [file],
            title: 'Snapshot',
            text: camera?.name ? `Snapshot from ${camera.name}` : 'Webcam snapshot',
          });
        } catch (shareErr) {
          if (shareErr.name !== 'AbortError') {
            triggerDownload(blob, filename);
          }
        }
      } else {
        triggerDownload(blob, filename);
      }
    } catch (err) {
      console.error('Snapshot download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleError = (e) => {
    const video = videoRef.current;
    let errorMsg = 'Video stream laden mislukt';
    
    if (video) {
      const error = video.error;
      if (error) {
        switch (error.code) {
          case error.MEDIA_ERR_ABORTED:
            errorMsg = 'Video laden geannuleerd';
            break;
          case error.MEDIA_ERR_NETWORK:
            errorMsg = 'Netwerkfout bij laden video - controleer of stream actief is';
            break;
          case error.MEDIA_ERR_DECODE:
            errorMsg = 'Video decode fout';
            break;
          case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMsg = 'Video formaat niet ondersteund - stream is mogelijk nog niet klaar. Probeer te vernieuwen.';
            break;
          default:
            errorMsg = `Video fout (code: ${error.code})`;
        }
      }
      console.error('Video error:', error, 'Stream URL:', streamUrl);
    }
    
    setError(errorMsg);
    setLoading(false);
  };

  return (
    <>
      <div className="video-player-container">
        <div className="video-wrapper">
          {loading && (
            <div className="video-loading">
              <div className="spinner"></div>
              <p>Stream laden...</p>
            </div>
          )}
          {error && (
            <div className="video-error">
              <p>⚠️ {error}</p>
            </div>
          )}
          {streamUrl && (
            <video
              ref={videoRef}
              className="video-element"
              muted
              playsInline
              autoPlay
              loop
              onLoadedMetadata={handleLoadedMetadata}
              onError={handleError}
              onLoadStart={() => {
                console.log('Video load started:', streamUrl);
                setLoading(true);
                setError(null);
              }}
              onCanPlay={() => {
                console.log('Video can play:', streamUrl);
                setLoading(false);
              }}
              onPlay={() => {
                console.log('Video started playing');
                setError(null);
              }}
              onClick={toggleFullscreen}
              style={{ display: error ? 'none' : 'block' }}
            />
          )}
        </div>
      </div>
      {streamUrl && !error && (
        <div className="video-player-actions">
          <button
            type="button"
            className="video-download-snapshot"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              downloadSnapshot();
            }}
            disabled={downloading || loading}
            title="Download huidige frame als afbeelding"
          >
            {downloading ? 'Bezig...' : 'Download snapshot'}
          </button>
        </div>
      )}
    </>
  );
};

export default VideoPlayer;

